import { valueToEnum } from 'common/EnumUtils';
import { formatShefSegments, MealType } from 'common/MealTypeUtils';
import CPG_SHEF_IDS from 'common/shef-profiles/CPGShefs';
import { ArrayType } from 'common/TypeUtilities';
import { urlDateFormat } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { first, isNil, zip } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import { Holiday, Packaging, SegmentFilter } from 'src/gqlReactTypings.generated.d';
import { useSetAvailableMealTypes } from 'src/pages/consumer/dish-first/explore/hooks';
import { useIsSoldOut } from 'src/pages/consumer/order/sold-out-notifications/useIsSoldOut';
import { getParam } from 'src/shared/utils/RouteUtilities';
import { useSelector } from 'src/store';
import { selectMenuItemCountById } from 'src/store/cart/selectors';
import { FoodItemAvailability } from 'src/store/cart/types';
import { match } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Section, sectionOrder } from './consts';
import { FoodItemWithOptionalQuantity, IConsumerOrderDishSelectionProps } from './types';
import { getFoodTypeSection, getHolidaySection, getMainItemsLabel, getSideItemsLabel } from './utils';

export const useGetCurrentMealType = (mealTypes: string[]) => {
  const { setAvailableMealTypes } = useSetAvailableMealTypes();
  useEffect(() => {
    setAvailableMealTypes(mealTypes as MealType[]);
  }, [mealTypes]);
};

export const getFirstMatchingAvailabilityOrFlexible = (
  deliveryDateTime: DateTime | undefined,
  foodItemAvailability: FoodItemAvailability[]
): FoodItemAvailability | undefined =>
  deliveryDateTime
    ? foodItemAvailability.find(
        ({ availabilityDate }) => new Date(availabilityDate).getTime() === deliveryDateTime.toMillis()
      )
    : first(foodItemAvailability);

export const getRemainingQuantities = (
  foodItems: FoodItemWithOptionalQuantity[],
  deliveryDateTime,
  menuCounts,
  numDishesRemaining
) =>
  foodItems.reduce<Record<number, number>>((quantities, foodItem) => {
    const availability = getFirstMatchingAvailabilityOrFlexible(deliveryDateTime, foodItem.availability);
    const numFoodItemRemaining = foodItem.quantityRemaining ?? availability?.numAvailable ?? 0;
    const numOfThisDishRemaining = (menuCounts[foodItem.id] ?? 0) + numDishesRemaining;

    return {
      ...quantities,
      [foodItem.id]: Math.min(numFoodItemRemaining, numOfThisDishRemaining),
    };
  }, {});

export const useConsumerOrderDishSelection = (props: IConsumerOrderDishSelectionProps) => {
  const {
    foodItems,
    isUnavailable,
    className,
    showOverlay,
    overlay,
    location,
    isEditingOrder,
    availabilityInfo,
    deliveryDate,
    nextDeliveryDate,
    shefId,
    shef,
    shefSegments,
    isShefAvailable,
    ...rest
  } = props;
  const exploreSegmentId = getParam(location, QueryParams.EXPLORE_SEGMENT_ID);
  const holidaysToSeparate: Set<Holiday> = match(exploreSegmentId)
    .with(SegmentFilter.Diwali, () => new Set([Holiday.Diwali]))
    .with(SegmentFilter.Thanksgiving, () => new Set([Holiday.Thanksgiving]))
    .with(SegmentFilter.WinterHoliday, () => new Set([Holiday.Hanukkah, Holiday.Christmas, Holiday.Kwanzaa]))
    .with(SegmentFilter.LunarNewYear, () => new Set([Holiday.LunarNewYear]))
    .otherwise(() => new Set());

  const { isSmallScreen } = useScreenContext();

  const dishDeliveryDate = isShefAvailable ? deliveryDate : nextDeliveryDate;

  const { numDishesRemaining } = useIsSoldOut({
    availabilityInfo,
    deliveryDate: dishDeliveryDate,
    shefId,
    isEditingOrder: !!isEditingOrder,
  });
  const { menuCounts } = useSelector((state) => {
    const deliveryUrlDate = dishDeliveryDate ? dishDeliveryDate.toFormat(urlDateFormat) : undefined;
    return {
      menuCounts: selectMenuItemCountById(
        isEditingOrder ? state.editableOrderCart : state.cart,
        shefId,
        deliveryUrlDate
      ),
    };
  });

  const remainingQuantities = useMemo(
    () => getRemainingQuantities(foodItems, dishDeliveryDate, menuCounts, numDishesRemaining),
    [foodItems, dishDeliveryDate, menuCounts, numDishesRemaining]
  );

  /**
   * Sort food items by whether they're sold out (sold out goes to the end).
   * The existing order between two items is preserved when they have both the same
   * sold out status
   */
  const sortBySoldOut = useCallback(
    (itemA: ArrayType<typeof foodItems>, itemB: ArrayType<typeof foodItems>) => {
      // First, see if exactly one is sold out, and sort that one lower:
      if (remainingQuantities[itemA.id] && !remainingQuantities[itemB.id]) {
        return -1;
      }
      if (remainingQuantities[itemB.id] && !remainingQuantities[itemA.id]) {
        return 1;
      }
      return 0;
    },
    [remainingQuantities]
  );

  // dish based capacity means user can experience the shefs selling in real time as they're adding
  // the items to their cart, useMemo here will make sure that we preserve the sort they saw on the first render
  const orderedFoodItems = useMemo(() => foodItems.sort(sortBySoldOut), [foodItems]);

  const orderedCategories = orderedFoodItems.map((item) => {
    if (!isNil(item.holiday) && holidaysToSeparate.has(item.holiday)) {
      return getHolidaySection(valueToEnum(Holiday, item.holiday)) ?? 'Holiday';
    }
    if (item.packaging === Packaging.Compostable) {
      return Section.Eco;
    }
    return getFoodTypeSection(item.foodType);
  });

  const dishesByCategory = zip(orderedFoodItems, orderedCategories);

  const sections = sectionOrder
    .map((section) => ({
      title: match(section)
        .with(Section.Main, () =>
          getMainItemsLabel({ isUnavailable, isSmallScreen, deliveryDateTime: dishDeliveryDate })
        )
        .with(Section.Side, () =>
          getSideItemsLabel({ isUnavailable, isSmallScreen, deliveryDateTime: dishDeliveryDate })
        )
        .otherwise(() => section.toString()),
      items: dishesByCategory
        .filter(([_, itemSection]) => itemSection === section)
        .map(([item]) => item)
        .filter((value: FoodItemWithOptionalQuantity | undefined): value is FoodItemWithOptionalQuantity =>
          Boolean(value)
        ),
      mealType: match(section)
        .with(Section.Main, () => MealType.Main)
        .with(Section.Side, () => MealType.Side)
        .otherwise(() => section.toString()),
    }))
    .filter(({ items }) => items.length);
  const segments = shefSegments?.filter((segment) => segment.content.total > 0);

  const mealTypes = CPG_SHEF_IDS.includes(shefId) ? [] : sections.map((section) => section.mealType);
  useGetCurrentMealType(
    segments?.length ? [...formatShefSegments(segments.map((segment) => segment.displayName)), ...mealTypes] : mealTypes
  );

  return {
    className,
    hasMultipleMealTypes: sections.length > 1,
    isUnavailable,
    location,
    menuCounts,
    remainingQuantities,
    overlay,
    shef,
    shefId,
    showOverlay,
    sections,
    isEditingOrder: isEditingOrder ?? false,
    deliveryDate,
    nextDeliveryDate,
    isShefAvailable: isShefAvailable && !!dishDeliveryDate,
    ...rest,
  };
};
